import React, { FormEvent, useEffect, useMemo, useState } from "react";
import type { User } from "firebase/auth";
import {
  Alert,
  Collapse,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import type {
  ExchangeSource,
  RateFieldPath,
} from "../../models/exchangeSource";
import {
  createExchangeSource,
  deleteExchangeSource,
  deleteExchangeSourceSchedulerJob,
  refreshExchangeSourceRate,
  resolveExchangeSourceRate,
  subscribeToExchangeSources,
  upsertExchangeSourceSchedulerJob,
  updateExchangeSource,
} from "../../api/exchangeSources";

const normalizeCurrency = (value: string): string => value.trim().toUpperCase();

const parseRateFieldPathInput = (raw: string): RateFieldPath => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return "rate";
  }

  // Only treat as JSON array if the entire input looks like JSON.
  // This avoids breaking bracket-path syntax like: [fuente=oficial].promedio
  if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
    const parsed = JSON.parse(trimmed) as unknown;
    if (!Array.isArray(parsed)) {
      throw new Error("rateFieldPath JSON must be an array.");
    }
    const valid = parsed.every(
      (item) => typeof item === "string" || typeof item === "number"
    );
    if (!valid) {
      throw new Error("rateFieldPath array must contain only strings/numbers.");
    }
    return parsed as Array<string | number>;
  }

  return trimmed;
};

const stringifyRateFieldPath = (path: RateFieldPath): string =>
  typeof path === "string" ? path : JSON.stringify(path);

const parseRequestHeadersInput = (
  raw: string
): Record<string, string> | null => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = JSON.parse(trimmed) as unknown;
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("requestHeaders must be a JSON object.");
  }
  const entries = Object.entries(parsed as Record<string, unknown>);
  const headers: Record<string, string> = {};
  for (const [key, value] of entries) {
    if (typeof key !== "string" || !key.trim()) {
      throw new Error("requestHeaders keys must be non-empty strings.");
    }
    if (typeof value === "string") {
      headers[key] = value;
    } else if (typeof value === "number" || typeof value === "boolean") {
      headers[key] = String(value);
    } else {
      throw new Error(
        "requestHeaders values must be strings, numbers, or booleans."
      );
    }
  }
  return Object.keys(headers).length > 0 ? headers : null;
};

const stringifyRequestHeaders = (
  headers?: Record<string, string> | null
): string =>
  headers && Object.keys(headers).length > 0
    ? JSON.stringify(headers, null, 2)
    : "";

const parseRequestBodyInput = (
  raw: string
): Record<string, unknown> | null => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = JSON.parse(trimmed) as unknown;
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Request body must be a JSON object.");
  }
  return parsed as Record<string, unknown>;
};

const stringifyRequestBody = (
  body?: Record<string, unknown> | null
): string =>
  body && typeof body === "object" && Object.keys(body).length > 0
    ? JSON.stringify(body, null, 2)
    : "";

const FREQUENCY_OPTIONS: Array<{ minutes: number; label: string }> = [
  { minutes: 5, label: "Every 5 minutes" },
  { minutes: 10, label: "Every 10 minutes" },
  { minutes: 15, label: "Every 15 minutes" },
  { minutes: 30, label: "Every 30 minutes" },
  { minutes: 60, label: "Every 1 hour" },
  { minutes: 120, label: "Every 2 hours" },
  { minutes: 360, label: "Every 6 hours" },
  { minutes: 720, label: "Every 12 hours" },
  { minutes: 1440, label: "Daily" },
];

const TIMEZONE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "UTC", label: "UTC" },
  { value: "America/Caracas", label: "America/Caracas (Venezuela)" },
  { value: "America/New_York", label: "America/New_York (US Eastern)" },
  { value: "America/Chicago", label: "America/Chicago (US Central)" },
  { value: "America/Denver", label: "America/Denver (US Mountain)" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles (US Pacific)" },
  { value: "America/Mexico_City", label: "America/Mexico_City" },
  { value: "America/Bogota", label: "America/Bogota" },
  { value: "America/Lima", label: "America/Lima" },
  { value: "America/Santiago", label: "America/Santiago" },
  {
    value: "America/Argentina/Buenos_Aires",
    label: "America/Argentina/Buenos_Aires",
  },
  { value: "Europe/London", label: "Europe/London" },
  { value: "Europe/Madrid", label: "Europe/Madrid" },
];

const ExchangeSourcesPage: React.FC<{ user: User }> = ({ user }) => {
  const [sources, setSources] = useState<ExchangeSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createHeadersError, setCreateHeadersError] = useState<string | null>(
    null
  );

  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editHeadersError, setEditHeadersError] = useState<string | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editing, setEditing] = useState<ExchangeSource | null>(null);
  const [editingHeadersRaw, setEditingHeadersRaw] = useState("");
  const [editingRegenHeadersRaw, setEditingRegenHeadersRaw] = useState("");
  const [editingRegenBodyRaw, setEditingRegenBodyRaw] = useState("");
  const [editRegenHeadersError, setEditRegenHeadersError] = useState<string | null>(
    null
  );
  const [editRegenBodyError, setEditRegenBodyError] = useState<string | null>(
    null
  );

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleSource, setScheduleSource] = useState<ExchangeSource | null>(
    null
  );
  const [scheduleMinutes, setScheduleMinutes] = useState<number>(15);
  const [scheduleTimeZone, setScheduleTimeZone] = useState<string>("UTC");
  const [scheduleTimeZonePreset, setScheduleTimeZonePreset] =
    useState<string>("UTC");

  const [testOpen, setTestOpen] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testSource, setTestSource] = useState<ExchangeSource | null>(null);
  const [testResult, setTestResult] = useState<{
    rate: number;
    rawValue: unknown;
    tokens: Array<string | number>;
  } | null>(null);

  const [newFromCurrency, setNewFromCurrency] = useState("USD");
  const [newToCurrency, setNewToCurrency] = useState("VES");
  const [newName, setNewName] = useState("");
  const [newRate, setNewRate] = useState<number>(0);
  const [newSourceUrl, setNewSourceUrl] = useState("");
  const [newRateFieldPath, setNewRateFieldPath] = useState("rate");
  const [newDataStructure, setNewDataStructure] = useState("JSON");
  const [showRateFieldPathHelp, setShowRateFieldPathHelp] = useState(false);
  const [newHeadersRaw, setNewHeadersRaw] = useState("");
  const [showKeyRegeneration, setShowKeyRegeneration] = useState(false);
  const [newKeyRegenerationUrl, setNewKeyRegenerationUrl] = useState("");
  const [newKeyRegenerationMethod, setNewKeyRegenerationMethod] =
    useState<string>("GET");
  const [newKeyRegenerationHeadersRaw, setNewKeyRegenerationHeadersRaw] =
    useState("");
  const [newKeyRegenerationResponseFieldPath, setNewKeyRegenerationResponseFieldPath] =
    useState("token");
  const [newKeyRegenerationTargetHeader, setNewKeyRegenerationTargetHeader] =
    useState("Authorization");
  const [createRegenHeadersError, setCreateRegenHeadersError] = useState<
    string | null
  >(null);
  const [newKeyRegenerationBodyRaw, setNewKeyRegenerationBodyRaw] =
    useState("");
  const [createRegenBodyError, setCreateRegenBodyError] = useState<string | null>(
    null
  );

  useEffect(() => {
    setLoading(true);
    const unsubscribe = subscribeToExchangeSources(
      (records) => {
        setSources(records);
        setLoading(false);
        setLoadError(null);
      },
      (error) => {
        setLoadError(error.message);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  const rows = useMemo(() => sources, [sources]);

  const openEdit = (source: ExchangeSource) => {
    setEditing(source);
    setEditError(null);
    setEditHeadersError(null);
    setEditRegenHeadersError(null);
    setEditingHeadersRaw(stringifyRequestHeaders(source.requestHeaders));
    setEditingRegenHeadersRaw(
      stringifyRequestHeaders(source.keyRegenerationRequestHeaders ?? null)
    );
    setEditingRegenBodyRaw(
      stringifyRequestBody(source.keyRegenerationRequestBody ?? null)
    );
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditing(null);
    setEditError(null);
    setEditHeadersError(null);
    setEditRegenHeadersError(null);
    setEditRegenBodyError(null);
    setEditingHeadersRaw("");
    setEditingRegenHeadersRaw("");
    setEditingRegenBodyRaw("");
  };

  const openSchedule = (source: ExchangeSource) => {
    setScheduleSource(source);
    setScheduleError(null);
    setScheduleMinutes(source.schedulerFrequencyMinutes ?? 15);
    const tz = source.schedulerTimeZone ?? "UTC";
    const isPreset = TIMEZONE_OPTIONS.some((opt) => opt.value === tz);
    setScheduleTimeZone(tz);
    setScheduleTimeZonePreset(isPreset ? tz : "__custom__");
    setScheduleOpen(true);
  };

  const closeSchedule = () => {
    setScheduleOpen(false);
    setScheduleSource(null);
    setScheduleError(null);
  };

  const openTest = (source: ExchangeSource) => {
    setTestSource(source);
    setTestResult(null);
    setTestError(null);
    setTestOpen(true);
  };

  const closeTest = () => {
    setTestOpen(false);
    setTestSource(null);
    setTestResult(null);
    setTestError(null);
  };

  const runTest = async () => {
    if (!testSource) {
      return;
    }
    setTestLoading(true);
    setTestError(null);
    setTestResult(null);
    try {
      const result = await resolveExchangeSourceRate({
        sourceUrl: testSource.sourceUrl,
        rateFieldPath: testSource.rateFieldPath,
        requestHeaders: testSource.requestHeaders ?? null,
      });
      setTestResult({
        rate: result.rate,
        rawValue: result.rawValue,
        tokens: result.rateFieldTokens,
      });
    } catch (error) {
      setTestError(
        error instanceof Error
          ? error.message
          : "Failed to resolve exchange rate."
      );
    } finally {
      setTestLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    if (!scheduleSource) {
      return;
    }
    setScheduleError(null);
    setScheduleSaving(true);
    try {
      await upsertExchangeSourceSchedulerJob(user, scheduleSource.id, {
        frequencyMinutes: scheduleMinutes,
        timeZone: scheduleTimeZone,
      });
      setRefreshMessage(
        `Scheduled ${scheduleSource.fromCurrency} → ${scheduleSource.toCurrency} refresh.`
      );
      closeSchedule();
    } catch (error) {
      setScheduleError(
        error instanceof Error ? error.message : "Failed to schedule job."
      );
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleDisableSchedule = async (source: ExchangeSource) => {
    const confirmed = window.confirm(
      `Disable auto refresh for ${source.fromCurrency} → ${source.toCurrency}?`
    );
    if (!confirmed) {
      return;
    }
    setRefreshError(null);
    setRefreshMessage(null);
    try {
      await deleteExchangeSourceSchedulerJob(user, source.id);
      setRefreshMessage(
        `Disabled auto refresh for ${source.fromCurrency} → ${source.toCurrency}.`
      );
    } catch (error) {
      setRefreshError(
        error instanceof Error ? error.message : "Failed to disable schedule."
      );
    }
  };

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCreateError(null);
    setCreateSuccess(null);
    setCreateHeadersError(null);
    setCreateRegenHeadersError(null);
    setCreateRegenBodyError(null);

    const fromCurrency = normalizeCurrency(newFromCurrency);
    const toCurrency = normalizeCurrency(newToCurrency);
    const name = newName.trim();
    const sourceUrl = newSourceUrl.trim();
    const dataStructure = newDataStructure.trim();

    if (!fromCurrency || !toCurrency) {
      setCreateError("Currencies are required.");
      return;
    }
    if (fromCurrency === toCurrency) {
      setCreateError("fromCurrency and toCurrency must be different.");
      return;
    }
    if (!sourceUrl) {
      setCreateError("sourceUrl is required.");
      return;
    }

    let rateFieldPath: RateFieldPath;
    try {
      rateFieldPath = parseRateFieldPathInput(newRateFieldPath);
    } catch (error) {
      setCreateError(
        error instanceof Error ? error.message : "Invalid rateFieldPath."
      );
      return;
    }

    let requestHeaders: Record<string, string> | null = null;
    try {
      requestHeaders = parseRequestHeadersInput(newHeadersRaw);
    } catch (error) {
      setCreateHeadersError(
        error instanceof Error ? error.message : "Invalid requestHeaders."
      );
      return;
    }

    const useRegen =
      showKeyRegeneration &&
      newKeyRegenerationUrl.trim() !== "" &&
      newKeyRegenerationTargetHeader.trim() !== "";
    let keyRegenerationResponseFieldPath: RateFieldPath | null = null;
    let keyRegenerationRequestHeaders: Record<string, string> | null = null;
    let keyRegenerationRequestBody: Record<string, unknown> | null = null;
    if (useRegen) {
      try {
        keyRegenerationResponseFieldPath = parseRateFieldPathInput(
          newKeyRegenerationResponseFieldPath
        );
      } catch (error) {
        setCreateError(
          error instanceof Error
            ? error.message
            : "Invalid keyRegenerationResponseFieldPath."
        );
        return;
      }
      try {
        keyRegenerationRequestHeaders = parseRequestHeadersInput(
          newKeyRegenerationHeadersRaw
        );
      } catch (error) {
        setCreateRegenHeadersError(
          error instanceof Error
            ? error.message
            : "Invalid keyRegenerationRequestHeaders."
        );
        return;
      }
      try {
        keyRegenerationRequestBody = parseRequestBodyInput(
          newKeyRegenerationBodyRaw
        );
        setCreateRegenBodyError(null);
      } catch (error) {
        setCreateRegenBodyError(
          error instanceof Error
            ? error.message
            : "Invalid keyRegenerationRequestBody."
        );
        return;
      }
    }

    setCreating(true);
    try {
      const id = await createExchangeSource({
        name,
        rate: Number.isFinite(newRate) ? newRate : 0,
        sourceUrl,
        fromCurrency,
        toCurrency,
        dataStructure,
        rateFieldPath,
        requestHeaders,
        ...(useRegen
          ? {
              keyRegenerationUrl: newKeyRegenerationUrl.trim(),
              keyRegenerationMethod:
                newKeyRegenerationMethod === "POST" ? "POST" : "GET",
              keyRegenerationRequestHeaders: keyRegenerationRequestHeaders,
              keyRegenerationRequestBody: keyRegenerationRequestBody,
              keyRegenerationResponseFieldPath:
                keyRegenerationResponseFieldPath!,
              keyRegenerationTargetHeader:
                newKeyRegenerationTargetHeader.trim(),
            }
          : {}),
      });
      setCreateSuccess(`Created exchange source ${id}.`);
      setNewSourceUrl("");
      setNewName("");
      setNewHeadersRaw("");
      if (useRegen) {
        setNewKeyRegenerationUrl("");
        setNewKeyRegenerationHeadersRaw("");
        setNewKeyRegenerationBodyRaw("");
      }
    } catch (error) {
      setCreateError(
        error instanceof Error
          ? error.message
          : "Failed to create exchange source."
      );
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (source: ExchangeSource) => {
    const confirmed = window.confirm(
      `Delete ${source.fromCurrency} → ${source.toCurrency} source?`
    );
    if (!confirmed) {
      return;
    }
    setRefreshError(null);
    setRefreshMessage(null);
    try {
      await deleteExchangeSource(source.id);
    } catch (error) {
      setRefreshError(
        error instanceof Error
          ? error.message
          : "Failed to delete exchange source."
      );
    }
  };

  const handleRefresh = async (source: ExchangeSource) => {
    setRefreshMessage(null);
    setRefreshError(null);
    setRefreshingId(source.id);
    try {
      const result = await refreshExchangeSourceRate(user, source.id);
      setRefreshMessage(
        `Refreshed ${source.fromCurrency} → ${source.toCurrency}: ${
          result.oldRate ?? "?"
        } → ${result.newRate}`
      );
    } catch (error) {
      setRefreshError(
        error instanceof Error
          ? error.message
          : "Failed to refresh exchange rate."
      );
    } finally {
      setRefreshingId(null);
    }
  };

  const handleSaveEdit = async () => {
    if (!editing) {
      return;
    }
    setEditError(null);
    setEditRegenHeadersError(null);
    setEditRegenBodyError(null);

    const fromCurrency = normalizeCurrency(editing.fromCurrency);
    const toCurrency = normalizeCurrency(editing.toCurrency);
    if (!fromCurrency || !toCurrency) {
      setEditError("Currencies are required.");
      return;
    }
    if (fromCurrency === toCurrency) {
      setEditError("fromCurrency and toCurrency must be different.");
      return;
    }
    if (!editing.sourceUrl.trim()) {
      setEditError("sourceUrl is required.");
      return;
    }

    let requestHeaders: Record<string, string> | null = null;
    try {
      requestHeaders = parseRequestHeadersInput(editingHeadersRaw);
      setEditHeadersError(null);
    } catch (error) {
      setEditHeadersError(
        error instanceof Error ? error.message : "Invalid requestHeaders."
      );
      return;
    }

    const regenUrl = (editing.keyRegenerationUrl ?? "").trim();
    const regenTarget = (editing.keyRegenerationTargetHeader ?? "").trim();
    const hasRegen = regenUrl !== "" && regenTarget !== "";
    let keyRegenerationRequestHeaders: Record<string, string> | null = null;
    let keyRegenerationRequestBody: Record<string, unknown> | null = null;
    if (hasRegen) {
      try {
        keyRegenerationRequestHeaders = parseRequestHeadersInput(
          editingRegenHeadersRaw
        );
        setEditRegenHeadersError(null);
      } catch (error) {
        setEditRegenHeadersError(
          error instanceof Error
            ? error.message
            : "Invalid keyRegenerationRequestHeaders."
        );
        return;
      }
      try {
        keyRegenerationRequestBody = parseRequestBodyInput(editingRegenBodyRaw);
        setEditRegenBodyError(null);
      } catch (error) {
        setEditRegenBodyError(
          error instanceof Error
            ? error.message
            : "Invalid keyRegenerationRequestBody."
        );
        return;
      }
    }

    setSavingEdit(true);
    try {
      await updateExchangeSource(editing.id, {
        name: (editing.name ?? "").trim(),
        rate: editing.rate,
        sourceUrl: editing.sourceUrl.trim(),
        fromCurrency,
        toCurrency,
        dataStructure: editing.dataStructure.trim(),
        rateFieldPath: editing.rateFieldPath,
        requestHeaders,
        keyRegenerationUrl: regenUrl || null,
        keyRegenerationMethod:
          editing.keyRegenerationMethod === "POST" ? "POST" : regenUrl ? "GET" : null,
        keyRegenerationRequestHeaders: hasRegen
          ? keyRegenerationRequestHeaders
          : null,
        keyRegenerationRequestBody: hasRegen
          ? keyRegenerationRequestBody
          : null,
        keyRegenerationResponseFieldPath: hasRegen
          ? editing.keyRegenerationResponseFieldPath ?? null
          : null,
        keyRegenerationTargetHeader: regenTarget || null,
      });
      closeEdit();
    } catch (error) {
      setEditError(
        error instanceof Error
          ? error.message
          : "Failed to update exchange source."
      );
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <>
      <header className="content__header mb-4">
        <div>
          <h1 className="page-header__title">
            Exchange sources
          </h1>
          <p className="page-header__lead">
            Manage external rate providers and refresh rates on demand.
          </p>
        </div>
      </header>

      {loadError ? <Alert variant="danger">{loadError}</Alert> : null}
      {refreshMessage ? (
        <Alert variant="success">{refreshMessage}</Alert>
      ) : null}
      {refreshError ? <Alert variant="warning">{refreshError}</Alert> : null}

      <Card className="shadow-sm mb-4">
        <Card.Header className="border-0">
          <Card.Title className="mb-0">Add source</Card.Title>
        </Card.Header>
        <Card.Body>
          {createSuccess ? (
            <Alert variant="success">{createSuccess}</Alert>
          ) : null}
          {createError ? <Alert variant="danger">{createError}</Alert> : null}

          <Form onSubmit={handleCreate}>
            <Row className="g-3">
              <Col md={3}>
                <Form.Group controlId="newFromCurrency">
                  <Form.Label>From currency</Form.Label>
                  <Form.Control
                    value={newFromCurrency}
                    onChange={(e) => setNewFromCurrency(e.target.value)}
                    maxLength={6}
                    disabled={creating}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="newToCurrency">
                  <Form.Label>To currency</Form.Label>
                  <Form.Control
                    value={newToCurrency}
                    onChange={(e) => setNewToCurrency(e.target.value)}
                    maxLength={6}
                    disabled={creating}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="newName">
                  <Form.Label>Source name (optional)</Form.Label>
                  <Form.Control
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="e.g. BCV Oficial, Monitor Dólar, Binance..."
                    disabled={creating}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group controlId="newRate">
                  <Form.Label>Initial rate</Form.Label>
                  <Form.Control
                    type="number"
                    value={Number.isFinite(newRate) ? newRate : 0}
                    onChange={(e) => setNewRate(Number(e.target.value))}
                    disabled={creating}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="newSourceUrl">
                  <Form.Label>sourceUrl</Form.Label>
                  <Form.Control
                    value={newSourceUrl}
                    onChange={(e) => setNewSourceUrl(e.target.value)}
                    placeholder="https://..."
                    disabled={creating}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="newRateFieldPath">
                  <div className="d-flex align-items-center justify-content-between">
                    <Form.Label className="mb-0">rateFieldPath</Form.Label>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0"
                      type="button"
                      onClick={() => setShowRateFieldPathHelp((prev) => !prev)}
                    >
                      {showRateFieldPathHelp ? "Hide guide" : "Show guide"}
                    </Button>
                  </div>
                  <Form.Control
                    value={newRateFieldPath}
                    onChange={(e) => setNewRateFieldPath(e.target.value)}
                    placeholder='rates.USD or ["rates","USD"]'
                    disabled={creating}
                  />
                  <Form.Text muted>
                    Use dot/brackets to extract a number from JSON. Then click{" "}
                    <strong>Test</strong> on the row to validate it.
                  </Form.Text>
                  <Collapse in={showRateFieldPathHelp}>
                    <div className="mt-3 p-3 rounded bg-body-secondary rounded">
                      <div className="fw-semibold mb-2">
                        How to set <code>rateFieldPath</code>
                      </div>
                      <div className="text-muted small mb-2">
                        The backend fetches <code>sourceUrl</code> (JSON) and
                        walks this path to find the numeric rate. If the
                        extracted value is a string, it will be parsed (commas
                        allowed).
                      </div>

                      <div className="small">
                        <div className="fw-semibold">
                          1) Simple field (object)
                        </div>
                        <div className="text-muted">
                          JSON: <code>{"{ rate: 311.88 }"}</code>
                        </div>
                        <div>
                          Path: <code>rate</code>
                        </div>
                      </div>

                      <hr />

                      <div className="small">
                        <div className="fw-semibold">2) Nested fields</div>
                        <div className="text-muted">
                          JSON: <code>{"{ data: { promedio: 311.88 } }"}</code>
                        </div>
                        <div>
                          Path: <code>data.promedio</code>
                        </div>
                      </div>

                      <hr />

                      <div className="small">
                        <div className="fw-semibold">3) Arrays (by index)</div>
                        <div className="text-muted">
                          JSON:{" "}
                          <code>
                            {"{ items: [ { rate: 1.23 }, { rate: 4.56 } ] }"}
                          </code>
                        </div>
                        <div>
                          Path: <code>items[0].rate</code> (first) ·{" "}
                          <code>items[1].rate</code> (second)
                        </div>
                      </div>

                      <hr />

                      <div className="small">
                        <div className="fw-semibold">
                          4) Array of maps (pick one of N options)
                        </div>
                        <div className="text-muted">
                          JSON (example):{" "}
                          <code>
                            {"[{ fuente: 'oficial', promedio: 311.88 }, ...]"}
                          </code>
                        </div>
                        <div>
                          Path: <code>[fuente=oficial].promedio</code> or{" "}
                          <code>[fuente=paralelo].promedio</code>
                        </div>
                        <div className="text-muted">
                          You can filter on any unique key, e.g.{" "}
                          <code>[nombre=Oficial].promedio</code>
                        </div>
                      </div>

                      <hr />

                      <div className="small">
                        <div className="fw-semibold">
                          5) JSON-array token format (optional)
                        </div>
                        <div className="text-muted">
                          Use this when you prefer explicit tokens.
                        </div>
                        <div>
                          Path: <code>["rates", "USD"]</code> or{" "}
                          <code>["items", 0, "rate"]</code>
                        </div>
                        <div className="text-muted">
                          Note: this must be valid JSON and must start with{" "}
                          <code>[</code> and end with <code>]</code>.
                        </div>
                      </div>
                    </div>
                  </Collapse>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="newRequestHeaders">
                  <Form.Label>requestHeaders (optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={newHeadersRaw}
                    onChange={(e) => {
                      setNewHeadersRaw(e.target.value);
                      setCreateHeadersError(null);
                    }}
                    placeholder={`{ "Authorization": "Bearer ..." }`}
                    disabled={creating}
                    isInvalid={Boolean(createHeadersError)}
                  />
                  <Form.Control.Feedback type="invalid">
                    {createHeadersError}
                  </Form.Control.Feedback>
                  <Form.Text muted>
                    JSON object of headers to include when fetching the exchange
                    rate.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="newDataStructure">
                  <Form.Label>dataStructure</Form.Label>
                  <Form.Control
                    value={newDataStructure}
                    onChange={(e) => setNewDataStructure(e.target.value)}
                    disabled={creating}
                  />
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0"
                  type="button"
                  onClick={() => setShowKeyRegeneration((prev) => !prev)}
                >
                  {showKeyRegeneration
                    ? "Hide key regeneration (optional)"
                    : "Configure key regeneration (optional)"}
                </Button>
                <Form.Text muted className="d-block small">
                  If the source uses an expiring key, we can fetch a new one from
                  a URL and retry when refresh fails.
                </Form.Text>
              </Col>
              <Collapse in={showKeyRegeneration}>
                <>
                  <Col md={12}>
                    <Form.Group controlId="newKeyRegenerationUrl">
                      <Form.Label>Key regeneration URL</Form.Label>
                      <Form.Control
                        value={newKeyRegenerationUrl}
                        onChange={(e) => setNewKeyRegenerationUrl(e.target.value)}
                        placeholder="https://.../token"
                        disabled={creating}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="newKeyRegenerationMethod">
                      <Form.Label>Method</Form.Label>
                      <Form.Select
                        value={newKeyRegenerationMethod}
                        onChange={(e) =>
                          setNewKeyRegenerationMethod(e.target.value)
                        }
                        disabled={creating}
                      >
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="newKeyRegenerationResponseFieldPath">
                      <Form.Label>Response field path (new key value)</Form.Label>
                      <Form.Control
                        value={newKeyRegenerationResponseFieldPath}
                        onChange={(e) =>
                          setNewKeyRegenerationResponseFieldPath(e.target.value)
                        }
                        placeholder="token or data.accessToken"
                        disabled={creating}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="newKeyRegenerationTargetHeader">
                      <Form.Label>Header to update</Form.Label>
                      <Form.Control
                        value={newKeyRegenerationTargetHeader}
                        onChange={(e) =>
                          setNewKeyRegenerationTargetHeader(e.target.value)
                        }
                        placeholder="Authorization"
                        disabled={creating}
                      />
                      <Form.Text muted>
                        e.g. Authorization, X-API-Key
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group controlId="newKeyRegenerationRequestHeaders">
                      <Form.Label>
                        Key regeneration request headers (optional)
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={newKeyRegenerationHeadersRaw}
                        onChange={(e) => {
                          setNewKeyRegenerationHeadersRaw(e.target.value);
                          setCreateRegenHeadersError(null);
                        }}
                        placeholder={`{ "Authorization": "Bearer ..." }`}
                        disabled={creating}
                        isInvalid={Boolean(createRegenHeadersError)}
                      />
                      <Form.Control.Feedback type="invalid">
                        {createRegenHeadersError}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group controlId="newKeyRegenerationRequestBody">
                      <Form.Label>
                        Key regeneration request body (optional, for POST)
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={newKeyRegenerationBodyRaw}
                        onChange={(e) => {
                          setNewKeyRegenerationBodyRaw(e.target.value);
                          setCreateRegenBodyError(null);
                        }}
                        placeholder={`{ "key": "value" }`}
                        disabled={creating}
                        isInvalid={Boolean(createRegenBodyError)}
                      />
                      <Form.Control.Feedback type="invalid">
                        {createRegenBodyError}
                      </Form.Control.Feedback>
                      <Form.Text muted>
                        JSON object sent as request body when method is POST.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </>
              </Collapse>

              <Col className="d-flex justify-content-end">
                <Button type="submit" disabled={creating}>
                  {creating ? "Creating..." : "Create"}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Header className="border-0 d-flex align-items-center justify-content-between">
          <Card.Title className="mb-0">Sources</Card.Title>
          <div className="text-muted small">{rows.length} total</div>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="d-flex align-items-center gap-2 text-muted">
              <Spinner animation="border" size="sm" />
              <span>Loading exchange sources...</span>
            </div>
          ) : rows.length === 0 ? (
            <div className="text-muted small">No exchange sources yet.</div>
          ) : (
            <Table responsive size="sm" className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Pair</th>
                  <th>Source</th>
                  <th>Rate</th>
                  <th>Auto refresh</th>
                  <th>rateFieldPath</th>
                  <th>sourceUrl</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((source) => (
                  <tr key={source.id}>
                    <td className="text-nowrap">
                      <div className="fw-semibold">
                        {source.fromCurrency} → {source.toCurrency}
                      </div>
                      <div className="text-muted small">{source.id}</div>
                    </td>
                    <td className="text-nowrap">
                      {source.name ? (
                        <div className="fw-semibold">{source.name}</div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-nowrap">{source.rate}</td>
                    <td className="text-nowrap">
                      {source.schedulerEnabled ? (
                        <div>
                          <div className="fw-semibold">
                            {FREQUENCY_OPTIONS.find(
                              (opt) =>
                                opt.minutes === source.schedulerFrequencyMinutes
                            )?.label ??
                              `Every ${source.schedulerFrequencyMinutes}m`}
                          </div>
                          <div className="text-muted small">
                            {source.schedulerTimeZone ?? "UTC"}
                          </div>
                          {source.schedulerJobName ? (
                            <div
                              className="text-muted small text-truncate"
                              style={{ maxWidth: 260 }}
                              title={source.schedulerJobName}
                            >
                              Job: {source.schedulerJobName}
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-nowrap">
                      <code>
                        {stringifyRateFieldPath(source.rateFieldPath)}
                      </code>
                    </td>
                    <td style={{ maxWidth: 420 }}>
                      <div className="text-truncate" title={source.sourceUrl}>
                        {source.sourceUrl}
                      </div>
                    </td>
                    <td className="text-muted small text-nowrap">
                      {source.updated ? source.updated.toLocaleString() : "—"}
                    </td>
                    <td className="text-nowrap">
                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => openEdit(source)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-success"
                          onClick={() => openTest(source)}
                        >
                          Test
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-info"
                          onClick={() => openSchedule(source)}
                        >
                          Schedule
                        </Button>
                        {source.schedulerEnabled ? (
                          <Button
                            size="sm"
                            variant="outline-warning"
                            onClick={() => handleDisableSchedule(source)}
                          >
                            Disable
                          </Button>
                        ) : null}
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={refreshingId === source.id}
                          onClick={() => handleRefresh(source)}
                        >
                          {refreshingId === source.id
                            ? "Refreshing..."
                            : "Refresh"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(source)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={scheduleOpen} onHide={closeSchedule} centered>
        <Modal.Header closeButton>
          <Modal.Title>Schedule auto refresh</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {scheduleError ? (
            <Alert variant="danger">{scheduleError}</Alert>
          ) : null}
          {scheduleSource ? (
            <>
              <div className="mb-3">
                <div className="fw-semibold">
                  {scheduleSource.fromCurrency} → {scheduleSource.toCurrency}
                </div>
                {scheduleSource.name ? (
                  <div className="text-muted small">{scheduleSource.name}</div>
                ) : null}
                <div className="text-muted small">{scheduleSource.id}</div>
              </div>
              <Row className="g-3">
                <Col md={8}>
                  <Form.Group controlId="scheduleFrequency">
                    <Form.Label>Frequency</Form.Label>
                    <Form.Select
                      value={scheduleMinutes}
                      onChange={(e) =>
                        setScheduleMinutes(Number(e.target.value))
                      }
                      disabled={scheduleSaving}
                    >
                      {FREQUENCY_OPTIONS.map((opt) => (
                        <option key={opt.minutes} value={opt.minutes}>
                          {opt.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="scheduleTimeZone">
                    <Form.Label>Time zone</Form.Label>
                    <Form.Select
                      value={scheduleTimeZonePreset}
                      onChange={(e) => {
                        const value = e.target.value;
                        setScheduleTimeZonePreset(value);
                        if (value !== "__custom__") {
                          setScheduleTimeZone(value);
                        }
                      }}
                      disabled={scheduleSaving}
                    >
                      {TIMEZONE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                      <option value="__custom__">Custom…</option>
                    </Form.Select>
                    {scheduleTimeZonePreset === "__custom__" ? (
                      <>
                        <Form.Control
                          className="mt-2"
                          value={scheduleTimeZone}
                          onChange={(e) => setScheduleTimeZone(e.target.value)}
                          placeholder="IANA timezone, e.g. UTC"
                          disabled={scheduleSaving}
                        />
                        <Form.Text muted>
                          Use an IANA timezone like <code>UTC</code> or{" "}
                          <code>America/Caracas</code>.
                        </Form.Text>
                      </>
                    ) : (
                      <Form.Text muted>
                        Uses IANA timezone ID: <code>{scheduleTimeZone}</code>
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={closeSchedule}
            disabled={scheduleSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveSchedule}
            disabled={scheduleSaving || !scheduleSource}
          >
            {scheduleSaving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={testOpen} onHide={closeTest} centered>
        <Modal.Header closeButton>
          <Modal.Title>Test rate extraction</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {testSource ? (
            <>
              <div className="mb-3">
                <div className="fw-semibold">
                  {testSource.fromCurrency} → {testSource.toCurrency}
                </div>
                {testSource.name ? (
                  <div className="text-muted small">{testSource.name}</div>
                ) : null}
                <div className="text-muted small">{testSource.id}</div>
              </div>

              <div className="mb-3">
                <div className="fw-semibold">sourceUrl</div>
                <div className="text-muted small text-break">
                  {testSource.sourceUrl}
                </div>
              </div>

              <div className="mb-3">
                <div className="fw-semibold">rateFieldPath</div>
                <code className="text-break">
                  {stringifyRateFieldPath(testSource.rateFieldPath)}
                </code>
                <div className="text-muted small mt-2">
                  Tip for array-of-maps: <code>[fuente=oficial].promedio</code>{" "}
                  or <code>[fuente=paralelo].promedio</code>
                </div>
              </div>

              <div className="mb-3">
                <div className="fw-semibold">requestHeaders</div>
                {testSource.requestHeaders ? (
                  <div
                    className="mt-2 p-2 rounded bg-body-secondary rounded"
                    style={{ overflowX: "auto" }}
                  >
                    <code style={{ whiteSpace: "pre" }}>
                      {stringifyRequestHeaders(testSource.requestHeaders)}
                    </code>
                  </div>
                ) : (
                  <div className="text-muted small">—</div>
                )}
              </div>
            </>
          ) : null}

          {testError ? <Alert variant="danger">{testError}</Alert> : null}
          {testResult ? (
            <Alert variant="success">
              <div className="fw-semibold">
                Resolved rate: {testResult.rate}
              </div>
              <div className="small">
                Tokens: <code>{JSON.stringify(testResult.tokens)}</code>
              </div>
              <div className="small">
                Raw value: <code>{JSON.stringify(testResult.rawValue)}</code>
              </div>
            </Alert>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={closeTest}
            disabled={testLoading}
          >
            Close
          </Button>
          <Button onClick={runTest} disabled={testLoading || !testSource}>
            {testLoading ? "Testing..." : "Run test"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={editOpen} onHide={closeEdit} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit exchange source</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editError ? <Alert variant="danger">{editError}</Alert> : null}
          {editing ? (
            <Form>
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group controlId="editName">
                    <Form.Label>Source name (optional)</Form.Label>
                    <Form.Control
                      value={editing.name ?? ""}
                      onChange={(e) =>
                        setEditing({ ...editing, name: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="editFromCurrency">
                    <Form.Label>From currency</Form.Label>
                    <Form.Control
                      value={editing.fromCurrency}
                      onChange={(e) =>
                        setEditing({ ...editing, fromCurrency: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="editToCurrency">
                    <Form.Label>To currency</Form.Label>
                    <Form.Control
                      value={editing.toCurrency}
                      onChange={(e) =>
                        setEditing({ ...editing, toCurrency: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group controlId="editRate">
                    <Form.Label>Rate</Form.Label>
                    <Form.Control
                      type="number"
                      value={editing.rate}
                      onChange={(e) =>
                        setEditing({ ...editing, rate: Number(e.target.value) })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group controlId="editSourceUrl">
                    <Form.Label>sourceUrl</Form.Label>
                    <Form.Control
                      value={editing.sourceUrl}
                      onChange={(e) =>
                        setEditing({ ...editing, sourceUrl: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group controlId="editRateFieldPath">
                    <Form.Label>rateFieldPath</Form.Label>
                    <Form.Control
                      value={stringifyRateFieldPath(editing.rateFieldPath)}
                      onChange={(e) => {
                        try {
                          const parsed = parseRateFieldPathInput(
                            e.target.value
                          );
                          setEditing({ ...editing, rateFieldPath: parsed });
                          setEditError(null);
                        } catch (error) {
                          setEditError(
                            error instanceof Error
                              ? error.message
                              : "Invalid rateFieldPath."
                          );
                        }
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group controlId="editRequestHeaders">
                    <Form.Label>requestHeaders (optional)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={editingHeadersRaw}
                      onChange={(e) => {
                        setEditingHeadersRaw(e.target.value);
                        setEditHeadersError(null);
                      }}
                      placeholder={`{ "Authorization": "Bearer ..." }`}
                      isInvalid={Boolean(editHeadersError)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {editHeadersError}
                    </Form.Control.Feedback>
                    <Form.Text muted>
                      JSON object of headers to include when fetching the
                      exchange rate.
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group controlId="editDataStructure">
                    <Form.Label>dataStructure</Form.Label>
                    <Form.Control
                      value={editing.dataStructure}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          dataStructure: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <hr />
                  <Form.Label className="text-muted">
                    Key regeneration (optional)
                  </Form.Label>
                  <Form.Text muted className="d-block small mb-2">
                    When refresh fails, fetch a new key from the URL and retry.
                  </Form.Text>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="editKeyRegenerationUrl">
                    <Form.Label>Key regeneration URL</Form.Label>
                    <Form.Control
                      value={editing.keyRegenerationUrl ?? ""}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          keyRegenerationUrl: e.target.value,
                        })
                      }
                      placeholder="https://.../token"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="editKeyRegenerationMethod">
                    <Form.Label>Method</Form.Label>
                    <Form.Select
                      value={editing.keyRegenerationMethod ?? "GET"}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          keyRegenerationMethod: e.target.value,
                        })
                      }
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="editKeyRegenerationResponseFieldPath">
                    <Form.Label>Response field path</Form.Label>
                    <Form.Control
                      value={stringifyRateFieldPath(
                        editing.keyRegenerationResponseFieldPath ?? "token"
                      )}
                      onChange={(e) => {
                        try {
                          const parsed = parseRateFieldPathInput(
                            e.target.value
                          );
                          setEditing({
                            ...editing,
                            keyRegenerationResponseFieldPath: parsed,
                          });
                          setEditError(null);
                        } catch (err) {
                          setEditError(
                            err instanceof Error
                              ? err.message
                              : "Invalid keyRegenerationResponseFieldPath."
                          );
                        }
                      }}
                      placeholder="token"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="editKeyRegenerationTargetHeader">
                    <Form.Label>Header to update</Form.Label>
                    <Form.Control
                      value={editing.keyRegenerationTargetHeader ?? ""}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          keyRegenerationTargetHeader: e.target.value,
                        })
                      }
                      placeholder="Authorization"
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="editKeyRegenerationRequestHeaders">
                    <Form.Label>
                      Key regeneration request headers (optional)
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={editingRegenHeadersRaw}
                      onChange={(e) => {
                        setEditingRegenHeadersRaw(e.target.value);
                        setEditRegenHeadersError(null);
                      }}
                      placeholder={`{ "Authorization": "Bearer ..." }`}
                      isInvalid={Boolean(editRegenHeadersError)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {editRegenHeadersError}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="editKeyRegenerationRequestBody">
                    <Form.Label>
                      Key regeneration request body (optional, for POST)
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={editingRegenBodyRaw}
                      onChange={(e) => {
                        setEditingRegenBodyRaw(e.target.value);
                        setEditRegenBodyError(null);
                      }}
                      placeholder={`{ "key": "value" }`}
                      isInvalid={Boolean(editRegenBodyError)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {editRegenBodyError}
                    </Form.Control.Feedback>
                    <Form.Text muted>
                      JSON object sent as request body when method is POST.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={closeEdit}
            disabled={savingEdit}
          >
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} disabled={savingEdit || !editing}>
            {savingEdit ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ExchangeSourcesPage;
